import React, { useMemo, useState } from 'react';

import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

import SectionLabel from '../../shared/SectionLabel';

import {
  ScrapePrvDowRow,
  ScrapePrvMonthRow,
  computeScrapePrvByDow,
  computeScrapePrvByMonth,
} from './otaParity/computeScrapePrv';
import { computePrvThresholdColor } from './otaParity/scrapePrvHeatColor';
import { computeSellVsMinOtaColor, computeSellVsMinOtaRange } from './otaParity/sellVsMinOtaHeatColor';
import { DailyDiscountStats, DailyMarketStats, ParsedOtaCsv } from './otaParity/types';

interface Props {
  parsed: ParsedOtaCsv;
  marketStats: Array<DailyMarketStats>;
  discountStats: Array<DailyDiscountStats>;
}

const DEFAULT_DISCOUNT_PCT = 20;

function fmtNum(value: number | null): string {
  return value != null ? Math.round(value).toLocaleString('en-AU') : '—';
}

function fmtPct(value: number | null): string {
  return value != null ? `${Math.round(value)}%` : '—';
}

function prvSx(value: number | null): { bgcolor: string; color?: string } | undefined {
  return value != null ? computePrvThresholdColor(value) : undefined;
}

function otaSx(
  value: number | null,
  range: ReturnType<typeof computeSellVsMinOtaRange>,
): { bgcolor: string; color: string } | undefined {
  return value != null && range ? computeSellVsMinOtaColor(value, range) : undefined;
}

export default function ScrapePrvSnapshot({ parsed, marketStats, discountStats }: Props) {
  const [discountPct, setDiscountPct] = useState(DEFAULT_DISCOUNT_PCT);

  const monthRows = useMemo(
    () => computeScrapePrvByMonth(parsed, marketStats, discountStats),
    [parsed, marketStats, discountStats],
  );
  const dowRows = useMemo(() => computeScrapePrvByDow(parsed, discountStats), [parsed, discountStats]);

  const monthOtaRange = useMemo(
    () => computeSellVsMinOtaRange(monthRows.map((r) => r.avgOta).filter((v): v is number => v != null)),
    [monthRows],
  );
  const dowOtaRange = useMemo(
    () => computeSellVsMinOtaRange(dowRows.map((r) => r.avgOta).filter((v): v is number => v != null)),
    [dowRows],
  );

  const indPrice = (row: ScrapePrvMonthRow) => (row.avgOta != null ? row.avgOta * (1 - discountPct / 100) : null);

  return (
    <Box>
      <SectionLabel>PRV snapshot</SectionLabel>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        By month
      </Typography>
      <TableContainer sx={{ mb: 3 }}>
        <Table size="small">
          <TableHead>
            {/* Discount input sits directly above Ind. Price, matching the workbook layout — only
                this column is wired to change when the discount level changes. */}
            <TableRow>
              <TableCell colSpan={4} sx={{ border: 0 }} />
              <TableCell align="right" sx={{ border: 0, pb: 0 }}>
                <TextField
                  label="Discount"
                  type="number"
                  size="small"
                  value={discountPct}
                  onChange={(e) => setDiscountPct(Number(e.target.value) || 0)}
                  InputProps={{ inputProps: { min: 0, max: 100, step: 1 }, endAdornment: '%' }}
                  sx={{ width: 120 }}
                />
              </TableCell>
              <TableCell colSpan={1} sx={{ border: 0 }} />
            </TableRow>
            <TableRow>
              <TableCell>Month</TableCell>
              <TableCell align="right">Median PRV %</TableCell>
              <TableCell align="right">Avg OTA</TableCell>
              <TableCell align="right">Low OTA</TableCell>
              <TableCell align="right">Ind. Price</TableCell>
              <TableCell align="right">LE Sell</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {monthRows.map((row: ScrapePrvMonthRow) => (
              <TableRow key={row.label}>
                <TableCell>{row.label}</TableCell>
                <TableCell align="right" sx={prvSx(row.medianPrvPct)}>
                  {fmtPct(row.medianPrvPct)}
                </TableCell>
                <TableCell align="right" sx={otaSx(row.avgOta, monthOtaRange)}>
                  {fmtNum(row.avgOta)}
                </TableCell>
                <TableCell align="right">{fmtNum(row.lowOta)}</TableCell>
                <TableCell align="right">{fmtNum(indPrice(row))}</TableCell>
                <TableCell align="right">{fmtNum(row.leSell)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        By day of week
      </Typography>
      <TableContainer>
        <Table size="small" sx={{ maxWidth: 520 }}>
          <TableHead>
            <TableRow>
              <TableCell>Day</TableCell>
              <TableCell align="right">Median PRV %</TableCell>
              <TableCell align="right">Avg OTA</TableCell>
              <TableCell align="right">LE Sell</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {dowRows.map((row: ScrapePrvDowRow) => (
              <TableRow key={row.label}>
                <TableCell>{row.label}</TableCell>
                <TableCell align="right" sx={prvSx(row.medianPrvPct)}>
                  {fmtPct(row.medianPrvPct)}
                </TableCell>
                <TableCell align="right" sx={otaSx(row.avgOta, dowOtaRange)}>
                  {fmtNum(row.avgOta)}
                </TableCell>
                <TableCell align="right">{fmtNum(row.leSell)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
